import { Link, NavLink } from "react-router-dom";
import { useLang } from "@/lib/lang-context";
import { useAuth } from "@/lib/auth-context";
import { Globe, LogIn, LogOut, User as UserIcon } from "lucide-react";

/**
 * Header — brand + nav + lang toggle + Google login/logout.
 *
 * Login URL is built dynamically with `window.location.origin` per playbook.
 * After Google completes auth, the user lands at `<origin>/#session_id=…`
 * which is detected synchronously by `AppRouter` and routed to AuthCallback.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
export const Header = () => {
  const { lang, setLang, t } = useLang();
  const { user, loading, logout } = useAuth();

  const onLogin = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href =
      "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 backdrop-blur-xl bg-[#F7F6F2]/85 border-b border-[#E6E4DD]"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-4 md:py-5 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-3 group min-w-0" data-testid="brand-link">
          <div className="h-8 w-8 border border-[#282C2A] flex items-center justify-center shrink-0">
            <span className="font-serif-display text-[#282C2A] text-xl leading-none">D</span>
          </div>
          <div className="leading-tight min-w-0">
            <div className="font-serif-display text-lg md:text-xl text-[#282C2A] tracking-tight truncate">
              {t("nav.brand")}
            </div>
            <div className="label-eyebrow hidden sm:block">est. 2026 — Apulia</div>
          </div>
        </Link>

        <nav className="flex items-center gap-1 md:gap-2">
          <NavLink
            to="/"
            end
            data-testid="nav-home"
            className={({ isActive }) =>
              `hidden sm:inline-block px-3 py-2 text-sm font-medium tracking-wide transition-colors ${
                isActive ? "text-[#4A5D23]" : "text-[#282C2A] hover:text-[#4A5D23]"
              }`
            }
          >
            {t("nav.home")}
          </NavLink>
          {user && (
            <NavLink
              to="/history"
              data-testid="nav-history"
              className={({ isActive }) =>
                `hidden sm:inline-block px-3 py-2 text-sm font-medium tracking-wide transition-colors ${
                  isActive ? "text-[#4A5D23]" : "text-[#282C2A] hover:text-[#4A5D23]"
                }`
              }
            >
              {t("nav.history")}
            </NavLink>
          )}
          {user && (
            <NavLink
              to="/plan"
              data-testid="nav-plan"
              className={({ isActive }) =>
                `hidden sm:inline-block px-3 py-2 text-sm font-medium tracking-wide transition-colors ${
                  isActive ? "text-[#4A5D23]" : "text-[#282C2A] hover:text-[#4A5D23]"
                }`
              }
            >
              {t("nav.plan")}
            </NavLink>
          )}

          <button
            data-testid="lang-toggle"
            onClick={() => setLang(lang === "it" ? "en" : "it")}
            className="ml-1 sm:ml-2 inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-2 text-xs font-semibold tracking-widest uppercase border border-[#282C2A] text-[#282C2A] hover:bg-[#282C2A] hover:text-white transition-colors"
          >
            <Globe size={14} strokeWidth={1.5} />
            {lang === "it" ? "EN" : "IT"}
          </button>

          {/* Auth slot */}
          {!loading && (user ? (
            <UserMenu user={user} onLogout={logout} label={t("nav.signOut")} />
          ) : (
            <button
              data-testid="nav-signin"
              onClick={onLogin}
              className="inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-2 text-xs font-semibold tracking-widest uppercase bg-[#282C2A] text-white hover:bg-[#4A5D23] border border-[#282C2A] hover:border-[#3B4A1C] transition-colors"
            >
              <LogIn size={13} strokeWidth={1.5} />
              <span className="hidden sm:inline">{t("nav.signIn")}</span>
            </button>
          ))}
        </nav>
      </div>
    </header>
  );
};

const UserMenu = ({ user, onLogout, label }) => (
  <div className="ml-1 sm:ml-2 inline-flex items-center gap-1.5 sm:gap-2" data-testid="nav-user-menu">
    {user.picture ? (
      <img
        src={user.picture}
        alt={user.name || user.email}
        className="h-7 w-7 rounded-full border border-[#E6E4DD]"
        referrerPolicy="no-referrer"
      />
    ) : (
      <div className="h-7 w-7 rounded-full bg-[#E6E4DD] flex items-center justify-center text-[#282C2A]">
        <UserIcon size={14} strokeWidth={1.5} />
      </div>
    )}
    <button
      data-testid="nav-signout"
      onClick={onLogout}
      title={label}
      className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 text-xs font-semibold tracking-widest uppercase border border-[#282C2A] text-[#282C2A] hover:bg-[#282C2A] hover:text-white transition-colors"
    >
      <LogOut size={13} strokeWidth={1.5} />
      <span className="hidden sm:inline">{label}</span>
    </button>
  </div>
);

export default Header;
